import {
	getVariationPrice,
	getVariationSalePrice,
} from '#lib/features/merch/utils/getVariationPrice/getVariationPrice.js';
import { getBookLabelText } from '#lib/websites/publishing/utils/getBookLabel/getBookLabel.js';
import { ShopRoutes } from '#lib/websites/shop/constants/routes.js';
import { formatPrice, CurrencyMap } from '$global/utils/formatPrice';
import { getImageUrl } from '$global/utils/getImageUrl';
import type { ShopCollectionDetailsPageData } from '$routes/shop/collections/[slug]/types';

export interface GridItem {
	line1: string;
	line2: string;
	line3: string;
	href: string;
	salePrice?: string | null;
	labelText: string | null;
	image: {
		url: string;
		width: number;
		height: number;
	};
}

export const getGridItems = (
	merchItems: ShopCollectionDetailsPageData['collection']['merch_items'],
	books: ShopCollectionDetailsPageData['collection']['publishing_books'],
	slugToExclude?: string
) => {
	const merchGridItems = (merchItems || []).map<GridItem>((merchItem) => {
		const inStock = !!(merchItem?.variations?.length
			? merchItem?.variations?.some(
					(variation) => variation?.related_Merch_Item_id?.product?.in_stock
				)
			: merchItem?.product?.in_stock);

		const image =
			merchItem?.image || merchItem?.parent_item?.[0]?.Merch_Item_id?.image;

		const variations = merchItem?.variations?.map(
			(v) => v?.related_Merch_Item_id
		) as Parameters<typeof getVariationPrice>[0];

		// Mirror the book sale price logic: show the sale price when one exists,
		// otherwise fall back to an empty string. For variation items the sale
		// price is the range across the variations on sale.
		const salePrice = (() => {
			if (!merchItem?.product) {
				return getVariationSalePrice(variations);
			}

			if (!merchItem?.product?.sale_price) {
				return '';
			}

			return formatPrice(
				merchItem?.product?.sale_price,
				merchItem?.product?.currency,
				true
			);
		})();

		return {
			line1: merchItem?.title || '',
			line2:
				(merchItem?.product
					? `${CurrencyMap[merchItem?.product?.currency?.toUpperCase()]}${merchItem?.product?.price}`
					: getVariationPrice(variations)) || '',

			line3: '',
			href: `${ShopRoutes.MerchProduct}/${merchItem?.slug}`,
			image: {
				url: getImageUrl(image?.filename_disk, '512px'),
				width: image?.width || 0,
				height: image?.height || 0,
			},
			labelText: inStock ? '' : 'OUT OF STOCK',
			salePrice: salePrice || '',
		};
	});

	const bookGridItems = (books || []).map<GridItem>((book) => ({
		line1: book?.title || '',
		line2:
			formatPrice(book?.product?.price, book?.product?.currency, true) || '',
		line3: '',
		href: `${ShopRoutes.Books}/${book?.slug}`,
		image: {
			url: getImageUrl(book?.featured_image?.filename_disk, '512px'),
			width: book?.featured_image?.width || 0,
			height: book?.featured_image?.height || 0,
		},
		labelText: getBookLabelText(book as NonNullable<typeof book>),
		salePrice: book?.product?.sale_price
			? formatPrice(book?.product?.sale_price, book?.product?.currency, true)
			: '',
	}));

	return [...bookGridItems, ...merchGridItems].filter((item) => {
		if (!slugToExclude) {
			return true;
		}

		const urlParts = item.href.split('/');
		const slug = urlParts[urlParts.length - 1];

		const includesSlug = slugToExclude.includes(slug);

		return !includesSlug;
	});
};
